import { filter, find, get, size } from 'lodash'
import { createSelector } from 'reselect'

import { getCurrentSpaceId, getOrganization } from '@/components/App/selectors'
import { getCurrentUserEmail } from '@/components/AuthenticationHandler/selectors'
import { Role } from '@/components/RolesContainer'

import { RootState } from '.'

const castToArray = (value: any) => (Array.isArray(value) ? value : [value])
const mapAndKeyResults = <T extends string | number | symbol, R>(values: T | T[], iteratee: (value: T) => R) => {
  if (!Array.isArray(values)) {
    return iteratee(values)
  }

  const results = values.reduce(
    (map, value) => {
      const error = iteratee(value)

      if (error) {
        map[value] = error
      }

      return map
    },
    {} as Record<T, R>
  )

  return size(results) > 0 ? results : null
}

const getMeta = (state: RootState) => state.__meta

export const getConfiguration = (state: RootState) => state.configuration
export const getCognitoUserPoolId = createSelector(getConfiguration, ({ cognito }) => cognito.userPoolId)
export const getEnabledFeatures = createSelector(getConfiguration, ({ features }) => features)
export const getTenantAPIHost = createSelector(getConfiguration, ({ apiHost }) => apiHost)

/**
 * Get all errors from the errors state
 *
 * @param state - Redux state.
 * @param types - Action types of the requests to validate.
 * @return errors
 */
export const getErrors = (state: RootState, types?: string[]) => {
  return state && state.errors
    ? Object.values(state.errors)
        .filter(e => e && e.error && (!types || types.includes(e.type)))
        .map(e => e.error)
    : []
}

/**
 * Returns the JSend error for a network request or `null` if the request hasn't completed yet or the request was
 * successful. If a list of request actions is provided and any has returned with an error, an object will be returned
 * with keys of request action type names and values of JSend errors.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {?string|object} JSend error or a map of JSend errors.
 */
export const getRequestError = (state: RootState, request: string | string[]) => {
  return mapAndKeyResults(request, req => get(getMeta(state), [req, 'error'], null))
}

/**
 * Returns the JSend error code for a network request or `null` if the request hasn't completed yet or the request was
 * successful. If a list of request actions is provided and any has returned with an error, an object will be returned
 * with keys of request action type names and values of error codes.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {?string|object} Error code or a map of error codes.
 */
export const getRequestErrorCode = (state: RootState, request: string | string[]) => {
  return mapAndKeyResults(request, req => get(getRequestError(state, req), 'code', null))
}

/**
 * Returns the JSend error message for a network request or `null` if the request hasn't completed yet or the request
 * was successful. If a list of request actions is provided and any has returned with an error, an object will be
 * returned with keys of request action type names and values of error messages.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {?string|object} Error message or a map of error messages.
 */
export const getRequestErrorMessage = (state: RootState, request: string | string[]) => {
  return mapAndKeyResults(request, req => get(getRequestError(state, req), 'message', null))
}

/**
 * Determines if a network request has returned yet. Doesn't evaluate if the request was successful or not. If a list of
 * request actions is provided, only a single request needs to be pending for this to evaluate to `true`.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {boolean} Whether any request is pending completion.
 */
export const isRequestPending = (state: RootState, request: string | string[]) => {
  return castToArray(request).some(req => get(getMeta(state), [req, 'isPending'], false))
}

/**
 * Determines if a network request has returned yet. Doesn't evaluate if the request was successful or not. If a list of
 * request actions is provided, all requests need to have completed for this to evaluate to `true`.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {boolean} Whether all requests have completed.
 */
export const isRequestComplete = (state: RootState, request: string | string[]) => {
  return castToArray(request).every(req => get(getMeta(state), [req, 'isSuccess'], false))
}

/**
 * Determines if a network request has returned with a JSend formatted error message. If a list of request actions is
 * provided, only a single request needs have returned with an error for this to evaluate to `true`.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {boolean} Whether any request returned with an error.
 */
export const isRequestError = (state: RootState, request: string | string[]) => getRequestError(state, request) !== null

/**
 * Determines if a network request returned with a successful, non-error state payload. If a list of request actions is
 * provided, all requests need have returned successful for this to evaluate to `true`.
 *
 * @param {object} state - Redux state.
 * @param {string|string[]} request - Action types of the requests to validate.
 * @return {boolean} Whether all requests returned successfully.
 */
export const isRequestSuccessful = (state: RootState, request: string | string[]) => {
  return castToArray(request).every(
    req => get(getMeta(state), [req, 'isSuccess'], false) && !isRequestError(state, req)
  )
}

export const getSpaceRoles = createSelector([(state: RootState) => state.roles.byId], roles => {
  const spaceRoles = Object.keys(roles).map(id => roles[id])
  return filter(spaceRoles, role => role.name !== Role.ORGANIZATION_ADMIN_ROLE)
})

export const getSpaceAdminRole = createSelector([(state: RootState) => state.roles.byId], roles => {
  const spaceRoles = Object.keys(roles).map(id => roles[id])
  return filter(spaceRoles, role => role.name === Role.SPACE_ADMIN_ROLE)[0]
})

export const getCurrentUser = createSelector(
  [getCurrentUserEmail, (state: RootState) => state.users.byId],
  (email, users) => {
    return find(users, user => user.email.toLowerCase() === email)
  }
)

export const getSpaces = createSelector(
  [getOrganization, (state: RootState) => state.spaces.byId],
  (organization, spaces) => {
    return Object.keys(spaces)
      .filter(id => organization.spaces.find(space => space.id === id))
      .map(id => spaces[id])
  }
)

export const getCurrentSpace = createSelector([getCurrentSpaceId, getSpaces], (currentSpaceId, spaces) => {
  return spaces.find(space => space.id === currentSpaceId)!
})

export const getCurrentOrganization = createSelector([getOrganization, getSpaces], (organization, spaces) => {
  const defaultSpace = spaces.find(space => space.default)
  const { id, name, supportUrl, logoUrl, companyCode, identityProviders, accountManagementConnectors } = organization

  return {
    id,
    name,
    supportUrl,
    logoUrl,
    companyCode,
    identityProviders,
    accountManagementConnectors,
    defaultSpace
  }
})

export const getNotificationSources = (state: RootState) => {
  const currentSpace = getCurrentSpace(state)
  const sources = currentSpace && currentSpace.notificationSources ? currentSpace.notificationSources : []
  return sources
    .map(s => ({ ...s, displayTitle: s.title || s.displayTitle }))
    .toSorted((a, b) => a.displayTitle.localeCompare(b.displayTitle))
}

export const getIdentityProviders = (state: RootState) => {
  const currentOrganization = getCurrentOrganization(state)

  if (currentOrganization) {
    return currentOrganization.identityProviders || []
  } else {
    return []
  }
}

export const getUsers = (state: RootState) => {
  return state.users
}

export const getRoles = createSelector([(state: RootState) => state.roles.byId], roles => Object.values(roles))

export const getOrgAdminRole = createSelector(
  [getRoles],
  roles => roles.find(({ name }) => name === Role.ORGANIZATION_ADMIN_ROLE) || ({} as { id: string; name: string })
)
